import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { api, type SearchHit, type SearchKind, type SearchResponse } from '../../lib/api'
import { CategoryMark } from '../spaces/CategoryMark'
import { layout } from '../../design/tokens'

/**
 * Recherche globale — décision L31.
 *
 * Elle traverse **tous** les espaces de l'utilisateur, et inclut les archives : quand on
 * cherche, c'est justement pour retrouver ce qu'on ne voit plus dans les listes. Chaque résultat
 * dit d'où il vient, espace et catégorie, sans quoi deux fiches de même titre seraient
 * indiscernables.
 *
 * La recherche par catégorie reste dans Récents : là, le filtre en place borne la portée.
 */
/**
 * Ce que chaque nature de résultat annonce.
 *
 * Écrit avant le nom de l'espace : c'est la première question qu'on se pose en parcourant une
 * liste mêlée — est-ce une fiche, un rendez-vous, une dépense ?
 */
const NATURES: Record<SearchKind, string> = {
  fiche: 'Fiche',
  agenda: 'Agenda',
  courses: 'Courses',
  depense: 'Dépense',
  cadeau: 'Cadeau',
}

/**
 * Où mène un résultat.
 *
 * Une fiche s'ouvre sur elle-même ; les autres natures n'ont pas d'écran propre et mènent à leur
 * module, dans le bon espace. Y conduire vaut mieux que de ne pas les proposer du tout — c'était
 * le cas jusqu'ici, la loupe ne cherchait que dans les fiches.
 */
function destination(hit: SearchHit): string {
  const espace = `/espace/${hit.spaceId}`

  switch (hit.kind) {
    case 'agenda':
      return `${espace}/agenda`
    case 'courses':
      return `${espace}/courses`
    case 'depense':
      return `${espace}/depenses`
    case 'cadeau':
      return `${espace}/cadeaux`
    default:
      return `${espace}/fiche/${hit.id}`
  }
}

export function SearchScreen() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const recherche = params.get('q') ?? ''
  const [saisie, setSaisie] = useState(recherche)

  const { data, isPending, isError } = useQuery<SearchResponse>({
    queryKey: ['recherche', recherche],
    queryFn: () => api.search(recherche),
    // Deux caractères minimum, comme le serveur : inutile de lui envoyer une requête qu'il
    // refusera.
    enabled: recherche.trim().length >= 2,
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: 32,
      }}
    >
      <header className="flex items-center gap-3" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="flex shrink-0 items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ←
        </button>
        <h1 className="font-serif text-screen">Rechercher</h1>
      </header>

      <form
        onSubmit={(evenement) => {
          evenement.preventDefault()
          setParams(saisie.trim() ? { q: saisie.trim() } : {})
        }}
        className="flex gap-2"
        style={{ paddingBottom: 16 }}
      >
        <input
          autoFocus
          value={saisie}
          onChange={(evenement) => setSaisie(evenement.target.value)}
          placeholder="Dans tous vos espaces"
          className="grow rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
          style={{ height: 56, fontSize: 17 }}
        />
        <button
          type="submit"
          className="rounded-button bg-accent px-4 text-card-title font-semibold text-card"
          style={{ height: 56 }}
        >
          Chercher
        </button>
      </form>

      {recherche.trim().length < 2 ? (
        <p className="text-body text-ink-muted">
          Tapez au moins deux lettres. La recherche porte sur le titre, le texte et le domaine, y
          compris dans les archives.
        </p>
      ) : isPending ? (
        <p className="text-body text-ink-muted">Recherche…</p>
      ) : isError ? (
        <p className="text-body text-ink-muted">La recherche a échoué.</p>
      ) : data && data.hits.length > 0 ? (
        <>
          <ul className="flex flex-col gap-2">
            {data.hits.map((hit) => (
              <li key={hit.id}>
                <Link
                  to={destination(hit)}
                  className="flex items-center gap-3 rounded-card border border-border bg-card p-3 text-ink"
                >
                  {hit.categoryInitials && hit.categoryHue !== null ? (
                    <CategoryMark
                      initials={hit.categoryInitials}
                      hue={hit.categoryHue}
                      size="small"
                    />
                  ) : null}
                  <span className="grow truncate">
                    <span className="block truncate text-card-title font-semibold">
                      {hit.title}
                    </span>
                    <span className="block truncate text-meta text-ink-muted">
                      {NATURES[hit.kind]}
                      {' · '}
                      {hit.spaceName}
                      {hit.subtitle ? ` · ${hit.subtitle}` : ''}
                      {hit.categoryName ? ` · ${hit.categoryName}` : ''}
                      {hit.domain ? ` · ${hit.domain}` : ''}
                    </span>
                  </span>
                  {hit.isArchived ? (
                    <span className="shrink-0 rounded-full bg-sunken px-2 text-meta text-ink-muted">
                      archivée
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>

          {data.truncated ? (
            <p className="mt-4 text-meta text-ink-muted">
              Seuls les premiers résultats sont montrés. Précisez votre recherche pour en voir
              d'autres.
            </p>
          ) : null}
        </>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">Rien ne correspond à « {recherche} ».</p>
          <p className="mt-2 text-body text-ink-muted">
            La recherche porte sur le titre, le texte et le domaine.
          </p>
        </div>
      )}
    </main>
  )
}
